const BASE_BATCH_NODE_UPDATE_RETRY_DELAY_MS = 1000;
const MAX_BATCH_NODE_UPDATE_RETRY_DELAY_MS = 8000;

function isPlainObjectRecord(value) {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function mergeCanvasNodeBatchMetadataValue(existingValue, nextValue) {
  if (!isPlainObjectRecord(existingValue) || !isPlainObjectRecord(nextValue)) {
    return nextValue;
  }

  const mergedValue = { ...existingValue };
  Object.entries(nextValue).forEach(([key, value]) => {
    mergedValue[key] = mergeCanvasNodeBatchMetadataValue(existingValue[key], value);
  });
  return mergedValue;
}

function toPositiveInteger(value) {
  const normalized = Number(value);
  return Number.isInteger(normalized) && normalized > 0 ? normalized : 0;
}

function getBrandStoryboard(metadata) {
  if (!isPlainObjectRecord(metadata)) return null;
  const storyboard = metadata.storyboard;
  if (!isPlainObjectRecord(storyboard)) return null;
  return isPlainObjectRecord(storyboard.brand_storyboard)
    ? storyboard.brand_storyboard
    : null;
}

function getVideoRevision(videoGeneration) {
  return isPlainObjectRecord(videoGeneration)
    ? toPositiveInteger(videoGeneration.revision)
    : 0;
}

function getPlanConfigRevision(visualPlan) {
  return isPlainObjectRecord(visualPlan) && isPlainObjectRecord(visualPlan.timing)
    ? toPositiveInteger(visualPlan.timing.video_config_revision)
    : 0;
}

function getPlanRevision(visualPlan) {
  return isPlainObjectRecord(visualPlan)
    ? toPositiveInteger(visualPlan.revision)
    : 0;
}

function isCoherentGenerationPair(videoGeneration, visualPlan) {
  if (!isPlainObjectRecord(videoGeneration) || !isPlainObjectRecord(visualPlan) || !isPlainObjectRecord(visualPlan.timing)) {
    return false;
  }
  const videoRevision = getVideoRevision(videoGeneration);
  const videoDuration = Number(videoGeneration.duration_seconds);
  const planDuration = Number(visualPlan.timing.duration_seconds);
  return videoRevision > 0
    && getPlanConfigRevision(visualPlan) === videoRevision
    && videoDuration > 0
    && videoDuration === planDuration;
}

function pickNewestCoherentGenerationPair(existingBoard, nextBoard) {
  const existingVideo = existingBoard.video_generation;
  const nextVideo = nextBoard.video_generation;
  const existingPlan = existingBoard.visual_plan;
  const nextPlan = nextBoard.visual_plan;
  const floor = Math.max(getVideoRevision(existingVideo), getPlanConfigRevision(existingPlan));
  const candidates = [];

  [[existingVideo, 0], [nextVideo, 1]].forEach(([video, videoSource]) => {
    [[existingPlan, 0], [nextPlan, 1]].forEach(([plan, planSource]) => {
      if (!isCoherentGenerationPair(video, plan)) return;
      const configRevision = getVideoRevision(video);
      if (configRevision < floor) return;
      candidates.push({
        video,
        plan,
        score: [configRevision, getPlanRevision(plan), -(videoSource + planSource)],
      });
    });
  });

  candidates.sort((left, right) => {
    for (let index = 0; index < left.score.length; index += 1) {
      if (left.score[index] !== right.score[index]) return right.score[index] - left.score[index];
    }
    return 0;
  });
  return candidates[0] || {
    video: isPlainObjectRecord(existingVideo) ? existingVideo : undefined,
    plan: isPlainObjectRecord(existingPlan) ? existingPlan : undefined,
  };
}

function mergeCanonicalBrandStoryboard(existingBoard, nextBoard) {
  const existingRevision = toPositiveInteger(existingBoard.revision);
  const nextRevision = toPositiveInteger(nextBoard.revision);
  if (existingRevision && nextRevision && nextRevision < existingRevision) {
    return existingBoard;
  }

  const existingGenerationFloor = Math.max(
    getVideoRevision(existingBoard.video_generation),
    getPlanConfigRevision(existingBoard.visual_plan),
  );
  const nextHasGenerationContract = isPlainObjectRecord(nextBoard.video_generation)
    || isPlainObjectRecord(nextBoard.visual_plan);
  if (
    nextRevision <= existingRevision
    && nextHasGenerationContract
    && (
      !isCoherentGenerationPair(nextBoard.video_generation, nextBoard.visual_plan)
      || getVideoRevision(nextBoard.video_generation) < existingGenerationFloor
      || getPlanConfigRevision(nextBoard.visual_plan) < existingGenerationFloor
    )
  ) {
    return existingBoard;
  }

  const mergedBoard = mergeCanvasNodeBatchMetadataValue(existingBoard, nextBoard);
  const generationPair = pickNewestCoherentGenerationPair(existingBoard, nextBoard);
  if (generationPair.video) mergedBoard.video_generation = generationPair.video;
  if (generationPair.plan) mergedBoard.visual_plan = generationPair.plan;
  return mergedBoard;
}

function mergeCanonicalStoryboardData(existingStoryboard, nextStoryboard) {
  if (!isPlainObjectRecord(existingStoryboard) || !isPlainObjectRecord(nextStoryboard)) {
    return nextStoryboard;
  }
  const existingBoard = existingStoryboard.brand_storyboard;
  const nextBoard = nextStoryboard.brand_storyboard;
  if (!isPlainObjectRecord(existingBoard) || !isPlainObjectRecord(nextBoard)) {
    return nextStoryboard;
  }

  const mergedStoryboard = mergeCanvasNodeBatchMetadataValue(existingStoryboard, nextStoryboard);
  mergedStoryboard.brand_storyboard = mergeCanonicalBrandStoryboard(existingBoard, nextBoard);
  return mergedStoryboard;
}

function mergeCanvasNodeBatchMetadata(existingMetadata, nextMetadata) {
  if (nextMetadata === undefined) {
    return existingMetadata;
  }

  if (existingMetadata === undefined) {
    return nextMetadata;
  }

  const mergedMetadata = mergeCanvasNodeBatchMetadataValue(existingMetadata, nextMetadata);
  const existingBoard = getBrandStoryboard(existingMetadata);
  const nextBoard = getBrandStoryboard(nextMetadata);
  if (!existingBoard || !nextBoard || !isPlainObjectRecord(mergedMetadata)) {
    return mergedMetadata;
  }

  mergedMetadata.storyboard = mergeCanonicalStoryboardData(
    existingMetadata.storyboard,
    nextMetadata.storyboard,
  );
  return mergedMetadata;
}

function mergeQueuedCanvasNodeUpdate(existingUpdate, nextUpdate) {
  const mergedUpdate = {
    ...existingUpdate,
    ...nextUpdate,
  };

  const hasExistingMetadata = Object.prototype.hasOwnProperty.call(existingUpdate, 'metadata');
  const hasNextMetadata = Object.prototype.hasOwnProperty.call(nextUpdate, 'metadata');

  if (hasExistingMetadata && hasNextMetadata) {
    mergedUpdate.metadata = mergeCanvasNodeBatchMetadata(existingUpdate.metadata, nextUpdate.metadata);
  }

  return mergedUpdate;
}

function getBatchNodeUpdateRetryDelayMs(failureCount) {
  const normalizedFailureCount = Number.isFinite(failureCount) && failureCount > 0
    ? Math.floor(failureCount)
    : 1;

  return Math.min(
    BASE_BATCH_NODE_UPDATE_RETRY_DELAY_MS * (2 ** (normalizedFailureCount - 1)),
    MAX_BATCH_NODE_UPDATE_RETRY_DELAY_MS,
  );
}

/**
 * CanvasApiError is deliberately consumed by shape here so this helper can
 * stay usable from the Node regression harness without importing the browser
 * API module. Unknown/network failures remain retryable; explicit permanent
 * 4xx responses do not.
 */
function isCanvasNodeBatchUpdateRetryable(error) {
  if (!error || (typeof error !== 'object' && typeof error !== 'function')) {
    return true;
  }

  const status = Number(error.details?.httpStatus ?? error.status);
  if (Number.isFinite(status)) {
    return status === 408
      || status === 409
      || status === 425
      || status === 429
      || status >= 500;
  }

  if (typeof error.retryable === 'boolean') {
    return error.retryable;
  }

  return error.name !== 'AbortError';
}

/**
 * Pure queue transition shared by the store and the Node regression harness.
 * A failed in-flight request only owns the active failure/retry state when the
 * currently visible project can still retain that request's updates. Otherwise
 * the request becomes detached and must not block a newer project's queue.
 *
 * @param {{
 *   failedProjectId: string,
 *   currentProjectId: string | null,
 *   pendingProjectId?: string | null,
 *   retryProjectId?: string | null,
 *   retryCount?: number,
 *   needsFlush?: boolean,
 *   retryable?: boolean,
 *   maxAttempts?: number,
 * }} state
 */
function transitionCanvasNodeBatchQueueAfterFailure({
  failedProjectId,
  currentProjectId,
  pendingProjectId = null,
  retryProjectId = null,
  retryCount = 0,
  needsFlush = false,
  retryable = true,
  maxAttempts = 5,
}) {
  const normalizedRetryCount = Number.isInteger(retryCount) && retryCount > 0
    ? retryCount
    : 0;
  const normalizedMaxAttempts = Number.isInteger(maxAttempts) && maxAttempts > 0
    ? maxAttempts
    : 1;
  const failedAttempt = retryProjectId === failedProjectId
    ? normalizedRetryCount + 1
    : 1;
  const canRetainInActiveQueue = currentProjectId === failedProjectId
    && (!pendingProjectId || pendingProjectId === failedProjectId);
  const shouldRetryAutomatically = retryable && failedAttempt < normalizedMaxAttempts;
  const disposition = canRetainInActiveQueue
    ? (shouldRetryAutomatically ? 'active-retry' : 'active-manual')
    : (shouldRetryAutomatically ? 'detached-retry' : 'detached-manual');
  const clearsFailedProjectState = !canRetainInActiveQueue
    && retryProjectId === failedProjectId;

  return {
    failedAttempt,
    disposition,
    canRetainInActiveQueue,
    shouldRetryAutomatically,
    shouldFlushPendingProject: Boolean(
      needsFlush
      && pendingProjectId
      && pendingProjectId !== failedProjectId,
    ),
    nextRetryProjectId: canRetainInActiveQueue
      ? failedProjectId
      : (clearsFailedProjectState ? null : retryProjectId),
    nextRetryCount: canRetainInActiveQueue
      ? failedAttempt
      : (clearsFailedProjectState ? 0 : normalizedRetryCount),
  };
}

module.exports = {
  getBatchNodeUpdateRetryDelayMs,
  isCanvasNodeBatchUpdateRetryable,
  mergeCanonicalStoryboardData,
  mergeQueuedCanvasNodeUpdate,
  transitionCanvasNodeBatchQueueAfterFailure,
};
